'use client'

import { usePathname, useRouter } from 'next/navigation'

import { type Locale, locales } from '@/i18n'
import { cn } from '@/utilities/ui'
import { trackLanguageSwitch } from '@/utilities/analytics'

interface LanguageSwitcherProps {
  currentLocale: Locale
  variant?: 'default' | 'mobile'
}

export function LanguageSwitcher({ currentLocale, variant = 'default' }: LanguageSwitcherProps) {
  const router = useRouter()
  const pathname = usePathname()

  const switchLocale = (newLocale: Locale) => {
    // Track language switch
    if (newLocale !== currentLocale) {
      trackLanguageSwitch(currentLocale, newLocale)
    }

    // Remove current locale from pathname
    const segments = pathname.split('/').filter(Boolean)

    // Remove the first segment if it's a locale
    if (locales.includes(segments[0] as Locale)) {
      segments.shift()
    }

    // Build new path with new locale
    const pathWithoutLocale = segments.length > 0 ? `/${segments.join('/')}` : ''
    const newPath = `/${newLocale}${pathWithoutLocale}`

    router.push(newPath)
  }

  return (
    <div className="flex items-center gap-2 z-50">
      {locales.map((locale) => (
        <button
          key={locale}
          onClick={() => switchLocale(locale)}
          className={cn(
            'uppercase text-sm transition-colors',
            variant === 'mobile'
              ? currentLocale === locale
                ? 'font-bold text-foreground'
                : 'text-muted-foreground hover:text-foreground'
              : currentLocale === locale
                ? 'font-bold text-white dark:text-white'
                : 'text-white/70 dark:text-white/70 hover:text-white dark:hover:text-white',
          )}
          aria-label={`Switch to ${locale === 'uk' ? 'Ukrainian' : 'English'}`}
          aria-current={currentLocale === locale ? 'true' : undefined}
        >
          {locale}
        </button>
      ))}
    </div>
  )
}
